/**
 * DocumentPreviewModal — split view: file preview on left, extracted fields on right.
 * Inline PDF/image viewer for uploaded files; "Open in Drive" link for cloud files.
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Download, FileText, Cloud, Loader2, ExternalLink, Upload } from "lucide-react";
import { api } from "../../lib/api.ts";

interface DocumentPreviewModalProps {
  doc: {
    id: string;
    display_name: string;
    file_name: string;
    file_path: string;
    doc_type: string;
    mime_type: string | null;
    extraction_status?: string;
    cloud_file_id?: string;
  };
  projectId: string;
  onClose: () => void;
}

/** Construct a direct link to a cloud file's native viewer */
function getCloudViewUrl(filePath: string, cloudFileId?: string): string | null {
  if (!filePath?.startsWith("cloud://")) return null;
  const fileId = cloudFileId || filePath.split("/").pop();
  if (!fileId) return null;

  if (filePath.includes("google_drive")) {
    return `https://drive.google.com/file/d/${fileId}/view`;
  }
  if (filePath.includes("onedrive")) {
    // OneDrive doesn't have a universal direct link without webUrl from Graph API.
    // Fallback: open the OneDrive web app (user can find it from recent files).
    return `https://onedrive.live.com`;
  }
  return null;
}

function isPreviewable(mimeType: string | null): boolean {
  if (!mimeType) return false;
  return mimeType === "application/pdf" || mimeType.startsWith("image/");
}

export function DocumentPreviewModal({ doc, projectId, onClose }: DocumentPreviewModalProps) {
  const [fields, setFields] = useState<any[] | null>(null);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fileExists, setFileExists] = useState<boolean | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isCloud = doc.file_path?.startsWith("cloud://");
  const fileUrl = api.getDocumentFileUrl(projectId, doc.id);
  const cloudViewUrl = getCloudViewUrl(doc.file_path, doc.cloud_file_id);
  const canPreview = !isCloud && isPreviewable(doc.mime_type) && fileExists === true;

  useEffect(() => {
    api.getDocumentFields(projectId, doc.id)
      .then(f => setFields(f))
      .catch(() => setFields([]))
      .finally(() => setFieldsLoading(false));

    // Check if file is available for preview
    if (!isCloud && isPreviewable(doc.mime_type)) {
      fetch(fileUrl, { method: "HEAD" })
        .then(res => setFileExists(res.ok))
        .catch(() => setFileExists(false));
    } else {
      setFileExists(false);
    }
  }, [doc.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = doc.file_name;
    a.click();
  }

  async function handleReupload(file: File) {
    setUploading(true);
    try {
      await api.uploadDocument(projectId, file, doc.doc_type);
      setFileExists(true);
      // Reload fields in case extraction runs
      api.getDocumentFields(projectId, doc.id)
        .then(f => setFields(f))
        .catch(() => {});
    } catch {} finally {
      setUploading(false);
    }
  }

  const showSplitView = canPreview;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] bg-scrim/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-surface rounded-2xl border border-border shadow-xl flex flex-col overflow-hidden ${
          showSplitView ? "w-full max-w-5xl max-h-[90vh]" : "w-full max-w-lg max-h-[80vh]"
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {isCloud ? <Cloud size={18} className="text-info flex-shrink-0" /> : <FileText size={18} className="text-text-tertiary flex-shrink-0" />}
            <div className="min-w-0">
              <div className="text-[15px] font-medium truncate">{doc.display_name}</div>
              <div className="text-[12px] text-text-tertiary">{doc.doc_type.replace(/_/g, " ")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isCloud && cloudViewUrl && (
              <a
                href={cloudViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] text-info hover:bg-info/10 rounded-lg transition-colors"
              >
                <ExternalLink size={13} /> Open in {doc.file_path?.includes("google_drive") ? "Google Drive" : "OneDrive"}
              </a>
            )}
            {!isCloud && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); window.open(fileUrl, "_blank"); }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors"
                >
                  <ExternalLink size={13} /> Open
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors"
                >
                  <Download size={13} /> Download
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 overflow-hidden ${showSplitView ? "flex" : ""}`}>
          {/* Left: File preview */}
          {showSplitView && (
            <div className="flex-1 bg-scrim/30 overflow-auto border-r border-border">
              {doc.mime_type === "application/pdf" ? (
                <iframe
                  src={`${fileUrl}#toolbar=0&navpanes=0`}
                  className="w-full h-full min-h-[500px]"
                  title={doc.display_name}
                />
              ) : doc.mime_type?.startsWith("image/") ? (
                <div className="flex items-center justify-center p-4 h-full">
                  <img
                    src={fileUrl}
                    alt={doc.display_name}
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
              ) : null}
            </div>
          )}

          {/* Right (or only): Fields + info */}
          <div className={`overflow-y-auto px-5 py-4 ${showSplitView ? "w-80 flex-shrink-0" : ""}`}>
            {/* Cloud file notice */}
            {isCloud && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-info-light/20 border border-info/20 rounded-xl mb-4 text-[13px] text-info">
                <Cloud size={14} className="flex-shrink-0" />
                <span>
                  Stored in {doc.file_path?.includes("google_drive") ? "Google Drive" : "OneDrive"}
                  {cloudViewUrl && (
                    <>
                      {" — "}
                      <a href={cloudViewUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-info/80">
                        view original
                      </a>
                    </>
                  )}
                </span>
              </div>
            )}

            {/* Non-previewable or missing file */}
            {!isCloud && !canPreview && (
              <div className="px-3 py-2.5 bg-surface-secondary rounded-xl mb-4 text-[13px] text-text-tertiary">
                {fileExists === false && isPreviewable(doc.mime_type) ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={14} className="flex-shrink-0" />
                      File no longer available on server.
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-on-solid rounded-lg text-[13px] font-medium hover:bg-brand/80 transition-colors disabled:opacity-50"
                    >
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                      {uploading ? "Uploading..." : "Re-upload file"}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.xlsx,.doc,.xls,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleReupload(file);
                        e.target.value = "";
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="flex-shrink-0" />
                    Preview not available for {doc.mime_type || "this file type"}
                  </div>
                )}
              </div>
            )}

            {/* Extracted fields */}
            {fieldsLoading ? (
              <div className="flex items-center justify-center py-10 text-text-tertiary">
                <Loader2 size={18} className="animate-spin mr-2" /> Loading...
              </div>
            ) : fields && fields.length > 0 ? (
              <div>
                <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                  Extracted fields ({fields.length})
                </h3>
                <div className="space-y-1">
                  {fields.map((f: any) => (
                    <div key={f.field_id} className="flex items-center gap-2 px-3 py-2 bg-surface-secondary rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-text-tertiary">{f.label || f.field_id}</div>
                        <div className="text-[13px] font-medium truncate" title={f.value}>{f.value || "—"}</div>
                      </div>
                      {f.confidence != null && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${bandStyle(band(f.confidence * 100, "confidence")).bg} ${bandStyle(band(f.confidence * 100, "confidence")).text}`}>
                          {Math.round(f.confidence * 100)}%
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-text-tertiary">
                <FileText size={24} className="mx-auto mb-2 opacity-40" />
                <p className="text-[13px]">No fields extracted yet</p>
                {doc.extraction_status === "failed" && (
                  <p className="text-[12px] mt-1 text-warning-text">Extraction failed. Use Retry to re-extract.</p>
                )}
                {doc.extraction_status === "pending" && (
                  <p className="text-[12px] mt-1">Extraction pending</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
